/**
 * /guide/admin — Schedule Manager (auth-required, real backend CRUD)
 * Dark theme — matches Guide.jsx palette.
 *
 * Loads channels + programs from `/api/program-guide/*` and persists every
 * Add/Edit/Delete to the backend. New stations get a one-click "Seed
 * default schedule" CTA in the empty state.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Pencil, Trash2, Wand2, X, Search, Tv, RefreshCw, Sparkles, Code, Copy, Check, Globe, BarChart3, GripVertical,
} from "lucide-react";
import { GuideShell } from "./Guide";
import { appConfirm, appAlert } from "@/components/AppDialogHost";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const GENRE_OPTIONS = ["News", "Music", "Talk Show", "Sports", "Entertainment", "Education"];

// Dark-theme genre chip styles
function chipForGenre(genre) {
  const m = {
    News:        "bg-[#0d1e35] text-[#4a9eff] border border-[#1e3050]",
    Music:       "bg-[#0d2318] text-[#3ecf8e] border border-[#1f3a2a]",
    "Talk Show": "bg-[#1a1230] text-[#a78bfa] border border-[#2c1f4a]",
    Sports:      "bg-[#241a08] text-[#f59e0b] border border-[#3a2a10]",
  };
  return m[genre] || "bg-[#0a0d14] text-[#4a5068] border border-[#1e2130]";
}

// ─────────────────────────────────────────────────────────────────────────────
// API client
// ─────────────────────────────────────────────────────────────────────────────
async function apiCall(method, path, body) {
  const opts = {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  };
  const r = await fetch(`${API_BASE}/api${path}`, opts);
  if (r.status === 401) {
    throw new Error("Please sign in to manage the schedule.");
  }
  if (!r.ok) {
    let msg = `Request failed (${r.status})`;
    try {
      const data = await r.json();
      if (data?.detail) msg = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    } catch { /* ignore parse errors */ }
    throw new Error(msg);
  }
  if (r.status === 204) return null;
  return r.json();
}

const api = {
  listChannels:  () => apiCall("GET",    "/program-guide/channels"),
  listPrograms:  () => apiCall("GET",    "/program-guide/programs"),
  createProgram: (b) => apiCall("POST",  "/program-guide/programs", b),
  updateProgram: (id, b) => apiCall("PATCH", `/program-guide/programs/${id}`, b),
  deleteProgram: (id) => apiCall("DELETE", `/program-guide/programs/${id}`),
  seedDefaults:  () => apiCall("POST",  "/program-guide/seed-defaults"),
  myStation:     () => apiCall("GET",    "/stations/me"),
  embedStats:    () => apiCall("GET",    "/program-guide/embed-stats"),
  interestStats: () => apiCall("GET",    "/program-guide/interest-stats"),
  reorderChannels: (order) => apiCall("POST", "/program-guide/channels/reorder", { order }),
};

const DAY_OPTIONS = [
  { id: "mon", label: "Mon" }, { id: "tue", label: "Tue" }, { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" }, { id: "fri", label: "Fri" }, { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];
const ALL_DAYS = DAY_OPTIONS.map((d) => d.id);

// ─────────────────────────────────────────────────────────────────────────────
// CHANNELS REORDER PANEL — native HTML5 drag-and-drop
// ─────────────────────────────────────────────────────────────────────────────
function ChannelsReorderPanel({ channels, onReordered }) {
  const [items, setItems] = useState(channels);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Keep local state in sync when parent updates the list (e.g. after seed)
  useEffect(() => { setItems(channels); }, [channels]);

  const isDirty = items.map((c) => c.channel_id).join("|") !== channels.map((c) => c.channel_id).join("|");

  const onDragStart = (e, id) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    setOverId(id);
  };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    setItems((cur) => {
      const next = [...cur];
      const from = next.findIndex((c) => c.channel_id === dragId);
      const to = next.findIndex((c) => c.channel_id === targetId);
      if (from < 0 || to < 0) return cur;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDragId(null);
    setOverId(null);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.reorderChannels(items.map((c) => c.channel_id));
      onReordered?.(items);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => setItems(channels);

  return (
    <section
      className="mb-5 bg-[#0a0d14] rounded-xl p-5"
      style={{ border: "0.5px solid #1e2130" }}
      data-testid="channels-reorder-panel"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[#0a0d14] text-[#4a9eff] border border-[#1e3050] flex items-center justify-center">
            <GripVertical className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-[#c8ccd8]">Channels</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#4a9eff] font-semibold mt-0.5">
              // Drag to reorder
            </div>
          </div>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              data-testid="reorder-cancel"
              className="text-[11px] text-[#4a5068] hover:text-[#c8ccd8] px-2 py-1 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="reorder-save"
              className="text-[11px] font-medium bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] px-3 py-1.5 rounded-md transition disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save new order"}
            </button>
          </div>
        )}
      </div>
      {error && (
        <div className="mb-3 bg-[#2a1a1a] text-[#ff6b6b] border border-[#3d1f1f] rounded-md px-3 py-2 text-[12px]">
          {error}
        </div>
      )}
      <ul className="space-y-1.5" data-testid="reorder-list">
        {items.map((c, idx) => {
          const isDragging = dragId === c.channel_id;
          const isOver = overId === c.channel_id && !isDragging;
          return (
            <li
              key={c.channel_id}
              draggable
              onDragStart={(e) => onDragStart(e, c.channel_id)}
              onDragOver={(e) => onDragOver(e, c.channel_id)}
              onDrop={(e) => onDrop(e, c.channel_id)}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              data-testid={`reorder-item-${c.channel_id}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-grab active:cursor-grabbing transition ${
                isDragging ? "opacity-30 bg-[#0d1e35]" :
                isOver     ? "bg-[#0d1e35] border-[#1e4d7a]" :
                             "bg-[#0f1117] hover:bg-[#142435]"
              } border ${isOver ? "border-[#1e4d7a]" : "border-[#1e2130]"}`}
            >
              <GripVertical className="h-4 w-4 text-[#4a5068] shrink-0" />
              <span className="font-mono text-[10px] text-[#4a5068] w-6 shrink-0">#{idx + 1}</span>
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${chipForGenre({"news":"News","music":"Music","talk":"Talk Show","sport":"Sports"}[c.color] || "Music")}`}>
                {c.type}
              </span>
              <span className="text-[13px] font-medium text-[#c8ccd8] flex-1">{c.name}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LISTENER INTEREST PANEL — top hours / top programs by viewer clicks
// ─────────────────────────────────────────────────────────────────────────────
function InterestPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.interestStats()
      .then((d) => { if (!cancelled) setStats(d); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !stats) return null;
  if (stats.total_signals === 0) {
    return (
      <section
        className="mb-5 bg-[#0a0d14] rounded-xl p-5"
        style={{ border: "0.5px solid #2c1f4a" }}
        data-testid="interest-panel"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="h-7 w-7 rounded-md bg-[#1a1230] text-[#a78bfa] flex items-center justify-center">
            <BarChart3 className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-[#c8ccd8]">Listener interest</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#a78bfa] font-semibold mt-0.5">
              // Top hours by clicks
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[#4a5068] text-center py-2 mt-1">
          No clicks yet. Once viewers start tapping program cards on the public guide, the hottest time-slots will show up here.
        </p>
      </section>
    );
  }

  const maxHour = stats.top_hours[0]?.signals || 1;

  return (
    <section
      className="mb-5 bg-[#0a0d14] rounded-xl p-5"
      style={{ border: "0.5px solid #2c1f4a" }}
      data-testid="interest-panel"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[#1a1230] text-[#a78bfa] flex items-center justify-center">
            <BarChart3 className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-[#c8ccd8]">Listener interest</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#a78bfa] font-semibold mt-0.5">
              // {stats.total_signals.toLocaleString()} signals · {stats.signals_week} this week
            </div>
          </div>
        </div>
      </div>

      {/* Top hours horizontal bars */}
      <div className="mb-4" data-testid="top-hours-list">
        <div className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold mb-2">
          Top hours by clicks
        </div>
        <ul className="space-y-1.5">
          {stats.top_hours.map((h, i) => {
            const pct = Math.max(8, Math.round((h.signals / maxHour) * 100));
            return (
              <li key={h.hour} className="flex items-center gap-3" data-testid={`top-hour-${i}`}>
                <div className="font-mono text-[11px] text-[#a78bfa] w-16 shrink-0">{h.label}</div>
                <div className="flex-1 h-2 rounded-full bg-[#0f1117] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#a78bfa]"
                    style={{ width: `${pct}%`, transition: "width 0.4s ease" }}
                  />
                </div>
                <div className="text-[11px] text-[#a78bfa] font-mono w-12 text-right">{h.signals}</div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Top programs */}
      {stats.top_programs.length > 0 && (
        <div data-testid="top-programs-list">
          <div className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold mb-2">
            Top programs
          </div>
          <ul className="space-y-1">
            {stats.top_programs.map((p, i) => (
              <li key={p.program_id} className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-[#0f1117]" data-testid={`top-program-${i}`}>
                <div className="text-[12px] text-[#c8ccd8] truncate font-medium">{p.title}</div>
                <div className="text-[11px] text-[#a78bfa] font-mono">{p.signals}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DISTRIBUTION STATS PANEL — embed analytics for the active station
// ─────────────────────────────────────────────────────────────────────────────
function DistributionPanel({ onOpenEmbed }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.embedStats()
      .then((d) => { if (!cancelled) setStats(d); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !stats) return null;
  const isEmpty = stats.total_views === 0;

  return (
    <section
      className="mb-5 bg-[#0a0d14] rounded-xl p-5"
      style={{ border: "0.5px solid #1f3a2a" }}
      data-testid="distribution-panel"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[#0d2318] text-[#3ecf8e] flex items-center justify-center">
            <Globe className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-[#c8ccd8]">Distribution</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#3ecf8e] font-semibold mt-0.5">
              // Embed analytics
            </div>
          </div>
        </div>
        {!isEmpty && (
          <button
            type="button"
            onClick={onOpenEmbed}
            data-testid="distribution-embed-cta"
            className="text-[11px] text-[#3ecf8e] hover:underline inline-flex items-center gap-1"
          >
            Get embed snippet <Code className="h-3 w-3" />
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className="text-center py-6" data-testid="distribution-empty">
          <p className="text-[12px] text-[#4a5068] max-w-md mx-auto">
            Your schedule isn&apos;t embedded on any 3rd-party sites yet.{" "}
            <button
              type="button"
              onClick={onOpenEmbed}
              data-testid="distribution-empty-cta"
              className="text-[#3ecf8e] hover:underline"
            >
              Grab the iframe snippet
            </button>{" "}
            and drop it on your WordPress, Wix, or homepage to start tracking.
          </p>
        </div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="distribution-kpis">
            <Kpi label="Total views"     value={stats.total_views}     tint="#3ecf8e" />
            <Kpi label="Unique sites"    value={stats.unique_domains}  tint="#4a9eff" />
            <Kpi label="Views this week" value={stats.views_this_week} tint="#a78bfa" />
          </div>

          {/* Top domains list */}
          {stats.top_domains.length > 0 && (
            <div data-testid="distribution-top-domains">
              <div className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold mb-2">
                Top sites
              </div>
              <ul className="space-y-1.5">
                {stats.top_domains.map((d, i) => {
                  const max = stats.top_domains[0].views || 1;
                  const pct = Math.max(8, Math.round((d.views / max) * 100));
                  return (
                    <li key={d.domain} className="flex items-center gap-3" data-testid={`top-domain-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-[#c8ccd8] truncate font-medium">{d.domain}</div>
                        <div className="mt-1 h-1.5 rounded-full bg-[#0f1117] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-[#3ecf8e]"
                            style={{ width: `${pct}%`, transition: "width 0.4s ease" }}
                          />
                        </div>
                      </div>
                      <div className="text-[12px] text-[#3ecf8e] font-mono w-12 text-right">{d.views}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Kpi({ label, value, tint }) {
  return (
    <div
      className="bg-[#0f1117] rounded-lg p-3 text-center"
      style={{ border: "0.5px solid #1e2130" }}
    >
      <div className="text-2xl font-bold tracking-tight" style={{ color: tint }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold mt-1">
        {label}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMBED SNIPPET MODAL — copy-paste iframe code for 3rd-party sites
// ─────────────────────────────────────────────────────────────────────────────
function EmbedModal({ open, slug, onClose }) {
  const [copied, setCopied] = useState(false);
  const [height, setHeight] = useState(600);

  const embedUrl = slug
    ? `${window.location.origin}/embed/guide/${slug}`
    : "";
  const snippet = `<iframe
  src="${embedUrl}"
  width="100%"
  height="${height}"
  frameborder="0"
  style="border: 0; border-radius: 12px; background: #0f1117;"
  loading="lazy"
  title="Program Guide">
</iframe>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 guide-fadein">
      <div
        className="bg-[#0a0d14] rounded-xl shadow-2xl max-w-xl w-full"
        style={{ border: "0.5px solid #1e2130" }}
        data-testid="embed-modal"
      >
        <header className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "0.5px solid #1e2130" }}>
          <div>
            <h2 className="text-[15px] font-semibold text-[#c8ccd8]">Embed on your site</h2>
            <p className="text-[11px] text-[#4a5068] mt-0.5">Paste this snippet into any HTML page — WordPress, Wix, Squarespace, raw HTML.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="embed-modal-close"
            className="h-8 w-8 rounded-md text-[#4a5068] hover:bg-[#0f1117] hover:text-[#c8ccd8] flex items-center justify-center transition"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          {/* Height picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold">Embed height</label>
            <div className="flex items-center gap-2 mt-1">
              {[400, 500, 600, 800].map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setHeight(h)}
                  data-testid={`embed-height-${h}`}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-medium border transition ${
                    height === h
                      ? "bg-[#1a3a5c] text-[#4a9eff] border-[#1e4d7a]"
                      : "bg-[#0f1117] text-[#4a5068] border-[#1e2130] hover:text-[#c8ccd8]"
                  }`}
                >
                  {h}px
                </button>
              ))}
            </div>
          </div>

          {/* Snippet */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold">Iframe snippet</label>
            <pre
              className="mt-1 bg-[#0f1117] border border-[#1e2130] rounded-md p-3 text-[11px] text-[#c8ccd8] overflow-x-auto leading-relaxed font-mono whitespace-pre"
              data-testid="embed-snippet"
            >
{snippet}
            </pre>
          </div>

          {/* Preview link */}
          <div className="text-[11px] text-[#4a5068]">
            Live preview:{" "}
            <a
              href={embedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#4a9eff] hover:underline"
              data-testid="embed-preview-link"
            >
              {embedUrl}
            </a>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="embed-modal-cancel"
              className="px-4 py-2 rounded-md text-[12px] font-medium text-[#4a5068] hover:bg-[#0f1117] hover:text-[#c8ccd8] transition"
            >
              Close
            </button>
            <button
              type="button"
              onClick={copy}
              data-testid="embed-copy-btn"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-medium border transition ${
                copied
                  ? "bg-[#0d2318] text-[#3ecf8e] border-[#1f3a2a]"
                  : "bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border-[#1e4d7a]"
              }`}
            >
              {copied ? (
                <><Check className="h-3.5 w-3.5" /> Copied!</>
              ) : (
                <><Copy className="h-3.5 w-3.5" /> Copy snippet</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD / EDIT PROGRAM MODAL — dark theme
// ─────────────────────────────────────────────────────────────────────────────
function AddProgramModal({ open, initial, channels, onClose, onSave, saving, error }) {
  const defaultChannelId = channels[0]?.channel_id || "";
  const [form, setForm] = useState(initial || {
    channel_id: defaultChannelId, title: "", genre: "Talk Show",
    start_time: "18:00", end_time: "19:00", description: "",
    days_of_week: [...ALL_DAYS],
  });
  useEffect(() => {
    if (open) setForm(initial ? {
      ...initial,
      days_of_week: (initial.days_of_week && initial.days_of_week.length) ? initial.days_of_week : [...ALL_DAYS],
    } : {
      channel_id: defaultChannelId, title: "", genre: "Talk Show",
      start_time: "18:00", end_time: "19:00", description: "",
      days_of_week: [...ALL_DAYS],
    });
  }, [open, initial, defaultChannelId]);

  if (!open) return null;
  const isEdit = !!initial?.program_id;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (id) => setForm((f) => {
    const cur = new Set(f.days_of_week || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    return { ...f, days_of_week: ALL_DAYS.filter((d) => cur.has(d)) };
  });
  const selectAllDays = () => setForm((f) => ({ ...f, days_of_week: [...ALL_DAYS] }));
  const selectWeekdays = () => setForm((f) => ({ ...f, days_of_week: ["mon", "tue", "wed", "thu", "fri"] }));
  const selectWeekends = () => setForm((f) => ({ ...f, days_of_week: ["sat", "sun"] }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.channel_id) return;
    onSave(form);
  };

  const inputCls = "w-full bg-[#0f1117] border border-[#1e2130] rounded-md px-3 py-2 text-[13px] text-[#c8ccd8] placeholder-[#4a5068] focus:outline-none focus:border-[#4a9eff] transition";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 guide-fadein">
      <div className="bg-[#0a0d14] rounded-xl shadow-2xl max-w-lg w-full" style={{ border: "0.5px solid #1e2130" }} data-testid="program-modal">
        <header className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "0.5px solid #1e2130" }}>
          <h2 className="text-[15px] font-semibold text-[#c8ccd8]">
            {isEdit ? "Edit program" : "Add program"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="modal-close"
            className="h-8 w-8 rounded-md text-[#4a5068] hover:bg-[#0f1117] hover:text-[#c8ccd8] flex items-center justify-center transition"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Channel">
              <select
                value={form.channel_id}
                onChange={(e) => update("channel_id", e.target.value)}
                data-testid="field-channel"
                className={inputCls}
                required
              >
                {channels.map((c) => <option key={c.channel_id} value={c.channel_id} className="bg-[#0f1117]">{c.name}</option>)}
              </select>
            </Field>
            <Field label="Genre">
              <select
                value={form.genre}
                onChange={(e) => update("genre", e.target.value)}
                data-testid="field-genre"
                className={inputCls}
              >
                {GENRE_OPTIONS.map((g) => <option key={g} value={g} className="bg-[#0f1117]">{g}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Title">
            <input
              type="text"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="e.g. Tech & AI Weekly"
              data-testid="field-title"
              className={inputCls}
              required
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start time">
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => update("start_time", e.target.value)}
                data-testid="field-start"
                className={inputCls}
                required
              />
            </Field>
            <Field label="End time">
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => update("end_time", e.target.value)}
                data-testid="field-end"
                className={inputCls}
                required
              />
            </Field>
          </div>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={3}
              data-testid="field-description"
              className={`${inputCls} resize-none`}
              placeholder="A short blurb so viewers know what to expect."
            />
          </Field>

          {/* Days-of-week checkbox row */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold">Airs on</span>
              <div className="flex items-center gap-2 text-[10px]">
                <button type="button" onClick={selectAllDays}    data-testid="days-all"     className="text-[#4a9eff] hover:underline">All</button>
                <span className="text-[#4a5068]">·</span>
                <button type="button" onClick={selectWeekdays}   data-testid="days-weekdays" className="text-[#4a9eff] hover:underline">Weekdays</button>
                <span className="text-[#4a5068]">·</span>
                <button type="button" onClick={selectWeekends}   data-testid="days-weekends" className="text-[#4a9eff] hover:underline">Weekends</button>
              </div>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="days-row">
              {DAY_OPTIONS.map((d) => {
                const on = (form.days_of_week || []).includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleDay(d.id)}
                    data-testid={`day-toggle-${d.id}`}
                    className={`px-3 py-1.5 text-[11px] font-medium rounded-md border transition ${
                      on ? "bg-[#1a3a5c] text-[#4a9eff] border-[#1e4d7a]"
                         : "bg-[#0f1117] text-[#4a5068] border-[#1e2130] hover:text-[#c8ccd8]"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
          {error && (
            <div className="bg-[#2a1a1a] text-[#ff6b6b] border border-[#3d1f1f] rounded-md px-3 py-2 text-[12px]" data-testid="modal-error">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="modal-cancel"
              className="px-4 py-2 rounded-md text-[12px] font-medium text-[#4a5068] hover:bg-[#0f1117] hover:text-[#c8ccd8] transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="modal-save"
              className="px-4 py-2 rounded-md text-[12px] font-medium bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] transition disabled:opacity-50"
            >
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Add program")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN ADMIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function GuideAdmin() {
  const [channels, setChannels] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [stationSlug, setStationSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [pageError, setPageError] = useState("");

  const [filter, setFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [seeding, setSeeding] = useState(false);

  // Initial load
  const load = async () => {
    setLoading(true);
    setPageError("");
    setAuthError("");
    try {
      const [chs, prs, me] = await Promise.all([
        api.listChannels(),
        api.listPrograms(),
        api.myStation().catch(() => null),
      ]);
      setChannels(chs);
      setPrograms(prs);
      if (me?.name) {
        const slug = me.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        setStationSlug(slug || me.station_id);
      }
    } catch (e) {
      if (/sign in/i.test(e.message)) setAuthError(e.message);
      else setPageError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Auto-open the modal pre-filled if user came from the AI bar
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("prefill") === "ai" && channels.length > 0) {
      const talkLive = channels.find((c) => /talk/i.test(c.name)) || channels[0];
      setEditing({
        channel_id: talkLive.channel_id,
        title: "Tech & AI Weekly",
        genre: "Talk Show",
        start_time: "18:00", end_time: "19:00",
        description: "Today's AI news, broken down for radio listeners.",
      });
      setModalOpen(true);
    }
  }, [channels.length]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return programs;
    return programs.filter((p) =>
      (p.title || "").toLowerCase().includes(f) ||
      (p.genre || "").toLowerCase().includes(f) ||
      (channels.find((c) => c.channel_id === p.channel_id)?.name || "").toLowerCase().includes(f)
    );
  }, [programs, filter, channels]);

  const handleSave = async (p) => {
    setSaving(true);
    setModalError("");
    try {
      const payload = {
        channel_id: p.channel_id, title: p.title, genre: p.genre,
        description: p.description, start_time: p.start_time, end_time: p.end_time,
        days_of_week: (p.days_of_week && p.days_of_week.length) ? p.days_of_week : ALL_DAYS,
      };
      if (p.program_id) {
        const updated = await api.updateProgram(p.program_id, payload);
        setPrograms((cur) => cur.map((x) => x.program_id === updated.program_id ? updated : x));
      } else {
        const created = await api.createProgram(payload);
        setPrograms((cur) => [...cur, created]);
      }
      setModalOpen(false);
      setEditing(null);
    } catch (e) {
      setModalError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await appConfirm("Delete this program?")) return;
    try {
      await api.deleteProgram(id);
      setPrograms((cur) => cur.filter((p) => p.program_id !== id));
    } catch (e) {
      await appAlert(`Couldn't delete: ${e.message}`);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setPageError("");
    try {
      await api.seedDefaults();
      await load();
    } catch (e) {
      setPageError(e.message);
    } finally {
      setSeeding(false);
    }
  };

  const startAdd = () => {
    if (channels.length === 0) {
      setPageError("Create at least one channel before adding programs. Use “Seed default schedule” for a starter lineup.");
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };
  const startEdit = (p) => {
    setEditing(p);
    setModalOpen(true);
  };

  const generateAI = () => {
    // Pick channels for each suggestion (graceful fall-through if some don't exist on this station)
    const find = (re) => channels.find((c) => re.test(c.name)) || channels[0];
    setAiSuggestion({
      week: "Next week",
      items: [
        { day: "Mon", time: "18:00", title: "Tech & AI Weekly",   reason: "Sunday slot peaks 3× viewers",   channel: find(/talk|news/i) },
        { day: "Tue", time: "07:00", title: "Wake Up Worldbeat",  reason: "Morning drive-time gap",         channel: find(/music|vibe/i) },
        { day: "Wed", time: "20:00", title: "Climate Watch",      reason: "Trending search topic",          channel: find(/news/i) },
        { day: "Thu", time: "19:00", title: "Champions Tonight",  reason: "Match-day audience spike",       channel: find(/sport/i) },
        { day: "Fri", time: "23:00", title: "Late Night Lounge",  reason: "Underserved weekend timeslot",   channel: find(/music|vibe/i) },
      ].filter((s) => !!s.channel),
    });
  };

  // ─── Auth gate ───
  if (authError) {
    return (
      <GuideShell>
        <div
          className="bg-[#0a0d14] rounded-xl p-10 text-center max-w-md mx-auto"
          style={{ border: "0.5px solid #1e2130" }}
          data-testid="admin-auth-required"
        >
          <div className="h-12 w-12 mx-auto rounded-full bg-[#0d1e35] text-[#4a9eff] flex items-center justify-center mb-3">
            <Tv className="h-5 w-5" />
          </div>
          <h1 className="text-[16px] font-semibold text-[#c8ccd8]">Sign in to manage the schedule</h1>
          <p className="text-[12px] text-[#4a5068] mt-2">
            Only station owners can add, edit or remove programs. Public viewers see the schedule at <code className="text-[#a78bfa]">/guide</code>.
          </p>
          <a
            href="/login?next=/guide/admin"
            data-testid="admin-login-cta"
            className="inline-flex items-center mt-5 px-4 py-2 rounded-md bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] text-[12px] font-medium transition"
          >
            Sign in
          </a>
        </div>
      </GuideShell>
    );
  }

  return (
    <GuideShell>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-4">
          <div
            className="h-12 w-12 rounded-lg flex items-center justify-center bg-[#0d1e35]"
            style={{ border: "0.5px solid #1e4d7a", boxShadow: "0 0 24px -4px #4a9eff66" }}
          >
            <Tv className="h-6 w-6 text-[#4a9eff]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4a9eff] font-semibold mb-1">// Program Guide · Admin</div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-[#f0f2f8] tracking-tight leading-tight"
              data-testid="admin-page-title"
            >
              Schedule Manager
            </h1>
            <p className="text-[12px] text-[#4a5068] mt-1">
              {loading
                ? "Loading…"
                : `${programs.length} program${programs.length === 1 ? "" : "s"} across ${channels.length} channel${channels.length === 1 ? "" : "s"}.`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-[#4a5068] hover:text-[#c8ccd8] border border-[#1e2130] hover:bg-[#0a0d14] transition disabled:opacity-50"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={generateAI}
            disabled={channels.length === 0}
            data-testid="generate-ai"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium bg-transparent border border-[#1e4d7a] text-[#4a9eff] hover:bg-[#0d1e35] transition disabled:opacity-30"
          >
            <Wand2 className="h-3.5 w-3.5" /> Generate with AI
          </button>
          <button
            type="button"
            onClick={() => setEmbedOpen(true)}
            disabled={!stationSlug || channels.length === 0}
            data-testid="open-embed-modal"
            title="Embed this schedule on any 3rd-party site"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium bg-transparent border border-[#1f3a2a] text-[#3ecf8e] hover:bg-[#0d2318] transition disabled:opacity-30"
          >
            <Code className="h-3.5 w-3.5" /> Embed
          </button>
          <button
            type="button"
            onClick={startAdd}
            disabled={channels.length === 0}
            data-testid="add-program-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] transition disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add program
          </button>
        </div>
      </div>

      {pageError && (
        <div
          className="mb-4 bg-[#2a1a1a] text-[#ff6b6b] border border-[#3d1f1f] rounded-md px-4 py-3 text-[12px]"
          data-testid="page-error"
        >
          {pageError}
        </div>
      )}

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5068]" />
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by title, channel or genre…"
          data-testid="admin-search"
          className="w-full pl-9 pr-3 py-2 text-[13px] bg-[#0a0d14] border border-[#1e2130] rounded-md text-[#c8ccd8] placeholder-[#4a5068] focus:outline-none focus:border-[#4a9eff] transition"
        />
      </div>

      {/* Distribution / embed analytics — only when station has content */}
      {channels.length > 0 && stationSlug && (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="analytics-row">
          <DistributionPanel onOpenEmbed={() => setEmbedOpen(true)} />
          <InterestPanel />
        </div>
      )}

      {/* Drag-and-drop channel reordering */}
      {channels.length > 1 && (
        <ChannelsReorderPanel
          channels={channels}
          onReordered={(next) => setChannels(next)}
        />
      )}

      {/* AI suggestion panel */}
      {aiSuggestion && (
        <section
          className="mb-5 bg-[#0a0d14] rounded-xl p-4 guide-fadein"
          style={{ border: "0.5px solid #1e3050" }}
          data-testid="ai-suggestion-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-md bg-[#1a3a5c] text-[#4a9eff] flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-[#c8ccd8]">AI-generated schedule · {aiSuggestion.week}</div>
              <div className="text-[11px] text-[#4a5068]">{aiSuggestion.items.length} new program{aiSuggestion.items.length === 1 ? "" : "s"} informed by your audience analytics. Tap one to add it.</div>
            </div>
            <button
              type="button"
              onClick={() => setAiSuggestion(null)}
              className="h-7 w-7 rounded-md text-[#4a5068] hover:bg-[#0f1117] hover:text-[#c8ccd8] flex items-center justify-center transition"
              data-testid="dismiss-ai"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="ai-suggestion-list">
            {aiSuggestion.items.map((item, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => {
                    setEditing({
                      channel_id: item.channel.channel_id,
                      title: item.title,
                      genre: item.channel.type,
                      start_time: item.time,
                      end_time: `${(parseInt(item.time, 10) + 1).toString().padStart(2, "0")}:00`,
                      description: `${item.reason}.`,
                    });
                    setModalOpen(true);
                  }}
                  data-testid={`ai-item-${i}`}
                  className="w-full text-left bg-[#0f1117] hover:bg-[#0d1e35] border border-[#1e2130] hover:border-[#1e4d7a] rounded-md px-3 py-2 transition"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-[#4a9eff]">{item.day} · {item.time}</span>
                    <span className="text-[10px] text-[#4a5068]">{item.channel.name}</span>
                  </div>
                  <div className="text-[13px] font-semibold text-[#c8ccd8] mt-1">{item.title}</div>
                  <div className="text-[10px] text-[#4a5068] mt-1">{item.reason}</div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Empty / Loading / Table states */}
      {loading ? (
        <div
          className="bg-[#0a0d14] rounded-xl p-10 text-center"
          style={{ border: "0.5px dashed #1e2130" }}
          data-testid="admin-loading"
        >
          <RefreshCw className="h-5 w-5 text-[#4a9eff] animate-spin mx-auto mb-3" />
          <div className="text-[13px] text-[#4a5068]">Loading schedule…</div>
        </div>
      ) : channels.length === 0 ? (
        // No channels yet — first-run experience
        <div
          className="bg-[#0a0d14] rounded-xl p-10 text-center"
          style={{ border: "0.5px dashed #1e2130" }}
          data-testid="admin-no-channels"
        >
          <div className="h-12 w-12 mx-auto rounded-full bg-[#0d1e35] text-[#4a9eff] flex items-center justify-center mb-3">
            <Tv className="h-5 w-5" />
          </div>
          <div className="text-[14px] font-medium text-[#c8ccd8]">No channels yet for this station.</div>
          <p className="text-[12px] text-[#4a5068] mt-1 max-w-md mx-auto">
            Drop in a starter lineup with one click — 4 channels (News 24, Vibe FM, Talk Live, Sport Zone) and 16 sample programs. You can edit or replace everything afterwards.
          </p>
          <button
            type="button"
            onClick={handleSeed}
            disabled={seeding}
            data-testid="seed-defaults-btn"
            className="inline-flex items-center mt-5 px-4 py-2 rounded-md bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] text-[12px] font-medium transition disabled:opacity-50"
          >
            {seeding ? (
              <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Seeding…</>
            ) : (
              <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Seed default schedule</>
            )}
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="bg-[#0a0d14] rounded-xl p-10 text-center"
          style={{ border: "0.5px dashed #1e2130" }}
          data-testid="admin-empty"
        >
          <div className="h-12 w-12 mx-auto rounded-full bg-[#0d1e35] text-[#4a9eff] flex items-center justify-center mb-3">
            <Tv className="h-5 w-5" />
          </div>
          <div className="text-[14px] font-medium text-[#c8ccd8]">
            {filter ? "No programs match that filter." : "No programs yet."}
          </div>
          <p className="text-[12px] text-[#4a5068] mt-1">
            {filter ? "Try a different search." : "Click “Add program” to schedule your first show."}
          </p>
        </div>
      ) : (
        <div
          className="bg-[#0a0d14] rounded-xl overflow-hidden"
          style={{ border: "0.5px solid #1e2130" }}
          data-testid="admin-table"
        >
          <table className="w-full">
            <thead>
              <tr className="bg-[#0f1117] text-[10px] uppercase tracking-wider text-[#4a5068]">
                <th className="text-left px-4 py-2.5 font-semibold">Channel</th>
                <th className="text-left px-4 py-2.5 font-semibold">Title</th>
                <th className="text-left px-4 py-2.5 font-semibold">Genre</th>
                <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">Start–End</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const ch = channels.find((c) => c.channel_id === p.channel_id);
                return (
                  <tr
                    key={p.program_id}
                    className="hover:bg-[#0f1117] transition"
                    style={{ borderTop: "0.5px solid #1e2130" }}
                    data-testid={`admin-row-${p.program_id}`}
                  >
                    <td className="px-4 py-2.5 text-[12px] text-[#a0a8bd]">{ch?.name || "—"}</td>
                    <td className="px-4 py-2.5 text-[13px] font-medium text-[#c8ccd8]">{p.title}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${chipForGenre(p.genre)}`}>
                        {p.genre || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-[#a0a8bd] whitespace-nowrap font-mono">
                      {p.start_time} – {p.end_time}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => startEdit(p)}
                        data-testid={`edit-${p.program_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-[#4a9eff] hover:bg-[#0d1e35] rounded mr-1 transition"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.program_id)}
                        data-testid={`delete-${p.program_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-[#ff6b6b] hover:bg-[#2a1a1a] rounded transition"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AddProgramModal
        open={modalOpen}
        initial={editing}
        channels={channels}
        saving={saving}
        error={modalError}
        onClose={() => { setModalOpen(false); setEditing(null); setModalError(""); }}
        onSave={handleSave}
      />

      <EmbedModal
        open={embedOpen}
        slug={stationSlug}
        onClose={() => setEmbedOpen(false)}
      />
    </GuideShell>
  );
}
